// Wysyłka wpisów dokumentacji zdjęciowej (P5), wspólna dla formularza nowej rezerwacji
// (pages/reservation.js) i szczegółów rezerwacji w kalendarzu (ReservationFormView.js).
//
// Dawniej każda z tych pętli łapała błąd w `catch (_)` i liczyła tylko porażki, więc przyczyna
// z serwera (np. „Nieprawidłowy plik zdjęcia (dozwolone: jpg, png, webp)”) ginęła, a panel
// pokazywał samo „1 z 1 wpisów nie zapisało się.”. Do tego notatka znikała z formularza także
// wtedy, gdy wpis się nie zapisał (sprawdzone w symulatorze iOS 26.5, 27.09.2026).
//
// Oba formularze rzucają błąd w kształcie axios: { response: { status, data } }. Szczegóły
// rezerwacji używają fetch, więc same składają taki obiekt (przegląd 28.09.2026: druga ścieżka
// czytała tylko pole error i gubiła polskie message bramki rozmiaru S.16).

/** Kod HTTP porażki; 0, gdy odpowiedź nie dotarła (zerwane połączenie, uśpiona strona). */
export function statusBledu(e) {
  return (e && e.response && e.response.status) || 0;
}

// Kod maszynowy z serwera (csrf_invalid, auth, forbidden) nie jest zdaniem dla obsługi przy ladzie.
const KOD_MASZYNOWY = /^[a-z0-9]+(?:[_.-][a-z0-9]+)*$/;

/**
 * Przyczyna podana przez SERWER zdaniem dla człowieka albo pusty tekst. Nie oddajemy ani opisu
 * biblioteki („Load failed”, „Network Error”, „Request failed with status code 502”), ani kodu
 * maszynowego („csrf_invalid”): wołający podstawia wtedy własny opis (przegląd 28.09.2026).
 */
export function powodBledu(e) {
  const dane = e && e.response && e.response.data;
  if (!dane || typeof dane !== 'object') return '';
  const tekst = String(dane.error || dane.message || '').trim();
  return tekst && !KOD_MASZYNOWY.test(tekst) ? tekst : '';
}

/**
 * Rodzaj porażki, od którego zależy, co zostaje w formularzu i co mówi komunikat:
 * - bezOdpowiedzi: odpowiedź nie dotarła (status 0) albo pośrednik nie doczekał się aplikacji
 *   (502, 504). Serwer mógł zapisać wpis, więc przed ponowieniem trzeba sprawdzić listę;
 * - sesja: 401 i 403 (wygasła sesja, nieważny token CSRF). Plik jest dobry, zostaje;
 * - chwilowy: pozostałe 5xx, 408 i 429. Plik zostaje do ponowienia;
 * - odrzucony: pozostałe 4xx (400 zły format, 413 za duży plik). Z tym plikiem wpis nigdy się
 *   nie zapisze, więc plik znika z formularza, a notatka zostaje.
 */
export function rodzajBledu(e) {
  const status = statusBledu(e);
  if (!status || status === 502 || status === 504) return 'bezOdpowiedzi';
  if (status === 401 || status === 403) return 'sesja';
  if (status >= 500 || status === 408 || status === 429) return 'chwilowy';
  return 'odrzucony';
}

/**
 * Wysyła wpisy PO KOLEI (serwer liczy kolejność wpisów) i nie przerywa na pierwszej porażce.
 * Zwraca nieudane wpisy z rodzajem porażki i przyczyną podaną przez serwer.
 */
export async function wyslijPoKolei(wpisy, wyslij) {
  const nieudane = [];
  for (const wpis of wpisy) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await wyslij(wpis);
    } catch (e) {
      nieudane.push({ wpis, rodzaj: rodzajBledu(e), powod: powodBledu(e) });
    }
  }
  return nieudane;
}

/**
 * Co zostaje w formularzu po wysyłce: notatka, jeśli jej wpis się nie zapisał (niezależnie od
 * przyczyny, żeby nie trzeba jej było pisać od nowa), i zdjęcia, które warto wysłać ponownie.
 * Zdjęcie odrzucone przez serwer znika, bo z nim formularz nigdy by się nie zapisał; jego nazwa
 * trafia do komunikatu (usuniete), żeby było wiadomo, które zniknęło.
 */
export function pozostaleWFormularzu(nieudane) {
  const pliki = (warunek) => nieudane.filter(warunek).map(({ wpis }) => wpis.file).filter(Boolean);
  return {
    zostawNotatke: nieudane.some(({ wpis }) => Boolean(wpis.note)),
    pliki: pliki(({ rodzaj }) => rodzaj !== 'odrzucony'),
    usuniete: pliki(({ rodzaj }) => rodzaj === 'odrzucony'),
  };
}

/**
 * Przyczyna jednego wpisu, który na pewno się nie zapisał, jako fragment zdania dla obsługi.
 * Wpis bez odpowiedzi serwera ma osobne zdanie (mógł się zapisać), więc tu nie trafia.
 */
export function powodWpisu(nieudany, t) {
  switch (nieudany.rodzaj) {
    case 'sesja': return t('reservationForm.photos.reasonSession');
    case 'chwilowy': return nieudany.powod || t('reservationForm.photos.reasonConnection');
    default: return nieudany.powod || t('reservationForm.photos.reasonRejected');
  }
}

/**
 * Wszystkie różne przyczyny w kolejności wysyłki: mieszane porażki nie gubią żadnej z nich.
 * Kropkę na końcu stawia szablon komunikatu, więc zdejmujemy ją z przyczyn serwera
 * („Żądanie jest zbyt duże.”), żeby nie było dwóch.
 */
export function powodyDoKomunikatu(nieudane, t) {
  const powody = nieudane.map((n) => powodWpisu(n, t).replace(/[.\s]+$/, ''));
  return [...new Set(powody)].join('; ');
}

/**
 * Dzieli porażki na wpisy, które na pewno się nie zapisały, i wpisy bez potwierdzenia zapisu
 * (odpowiedź nie dotarła, więc mogły się zapisać). Jedno zdanie o obu naraz przeczyło samo sobie
 * („nie zapisało się… mógł się jednak zapisać”, pomiar 28.09.2026).
 */
function rozdziel(nieudane) {
  return {
    odrzucone: nieudane.filter(({ rodzaj }) => rodzaj !== 'bezOdpowiedzi'),
    bezPotwierdzenia: nieudane.filter(({ rodzaj }) => rodzaj === 'bezOdpowiedzi'),
  };
}

/**
 * Komunikat po wysyłce w szczegółach rezerwacji: przyczyny wpisów, które się nie zapisały, osobno
 * wpisy bez potwierdzenia zapisu, nazwy zdjęć usuniętych z formularza i, gdy odpowiedź nie
 * dotarła, prośba o sprawdzenie listy przed ponowieniem (serwer nie ma jeszcze klucza
 * idempotencji, ROADMAP P5.7).
 */
export function komunikatSzczegolow(nieudane, wszystkich, t) {
  const { odrzucone, bezPotwierdzenia } = rozdziel(nieudane);
  const czesci = [];
  if (odrzucone.length) {
    czesci.push(t('reservationForm.photos.partialFailure', {
      failed: odrzucone.length, total: wszystkich, reason: powodyDoKomunikatu(odrzucone, t),
    }));
  }
  if (bezPotwierdzenia.length) {
    czesci.push(t('reservationForm.photos.unconfirmed', { count: bezPotwierdzenia.length, total: wszystkich }));
  }
  const { usuniete } = pozostaleWFormularzu(nieudane);
  if (usuniete.length) {
    czesci.push(t('reservationForm.photos.removedFiles', { files: usuniete.map((f) => f.name || '?').join(', ') }));
  }
  if (bezPotwierdzenia.length) czesci.push(t('reservationForm.photos.checkListBeforeRetry'));
  return czesci.join(' ');
}

/**
 * Komunikat po zapisie nowej rezerwacji. Tu wpisy nie zostają w żadnym formularzu (panel
 * przechodzi do kalendarza), więc bez „spróbuj ponownie”; gdy odpowiedź nie dotarła, wpis mógł
 * się zapisać i trzeba sprawdzić dokumentację rezerwacji, a nie dodawać go od nowa na ślepo.
 */
export function komunikatNowejRezerwacji(nieudane, wszystkich, t) {
  const { odrzucone, bezPotwierdzenia } = rozdziel(nieudane);
  if (!bezPotwierdzenia.length) {
    return t('reservation.save.docsPartialFailure', {
      failed: odrzucone.length, total: wszystkich, reason: powodyDoKomunikatu(odrzucone, t),
    });
  }
  if (!odrzucone.length) {
    return t('reservation.save.docsUnconfirmed', { failed: bezPotwierdzenia.length, total: wszystkich });
  }
  return t('reservation.save.docsMixed', {
    failed: odrzucone.length, total: wszystkich, reason: powodyDoKomunikatu(odrzucone, t),
    unconfirmed: bezPotwierdzenia.length,
  });
}
